import { useMemo } from "react";
import { createClient } from "@supabase/supabase-js";
import { ProductJsonLd } from "next-seo";
import { useQuery } from "react-query";

import { ProductByHandleQuery } from "~lib/shopify/sdk";

interface Props {
  product: ProductByHandleQuery["productByHandle"];
  sku: string;
}

export const ProductStructuredData = ({ product, sku }: Props) => {
  const supabase = createClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL,
    process.env.NEXT_PUBLIC_SUPABASE_KEY
  );
  const productId = useMemo(
    () => product?.id?.slice(product?.id.lastIndexOf("/") + 1),
    [product?.id]
  );
  const { data: score } = useQuery(
    ["productScore", productId],
    async () => {
      const {
        data: [score],
      } = await supabase.rpc("product_full_stats", {
        p_productid: productId,
      });
      return score;
    },
    { refetchOnWindowFocus: false }
  );
  return (
    <ProductJsonLd
      productName={product?.title}
      images={product?.images?.edges
        ?.filter(({ node }) => node.mediaContentType === "IMAGE")
        .map(({ node }) => node.image.originalSrc)}
      description={product?.description}
      brand={product?.vendor}
      offers={
        product?.variants?.edges?.map(({ node }) => ({
          name: node.selectedOptions?.map(({ value }) => value).join(", "),
          price: parseFloat(node.priceV2?.amount),
          priceCurrency: node.priceV2?.currencyCode,
          itemCondition: "http://schema.org/NewCondition",
          availability: `http://schema.org/${
            node?.availableForSale ? "InStock" : "OutOfStock"
          }`,
          url: `/products/${product.handle}?variantId=${node.id}`,
          sku: node?.sku,
        })) as any
      }
      sku={sku}
      {...(score && score?.rewiew_count
        ? {
            aggregateRating: {
              ratingValue: score?.average_rating,
              reviewCount: score?.review_count,
              bestRating: "5",
            },
          }
        : {})}
    />
  );
};
